import * as Enumerable from 'linq';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Activity, ActivityEntityName, DomainType, Entity, ItemDeletedResponse, MetadataAttachment } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';

@Injectable()
export class FilesService {

    private _files = new BehaviorSubject<MetadataAttachment[]>([]);
    public readonly files$ = this._files.asObservable();
    public get files(): MetadataAttachment[] { return this._files.value; }

    public _fileChanged = new BehaviorSubject<MetadataAttachment>(null);
    public readonly fileChanged$ = this._fileChanged.asObservable();

    private get api() {
        // The portal is mounted on the tenant host at /clients, so the hostname does not identify the app.
        const domainType = ConfigService.environment.domainType as DomainType;
        switch (domainType) {
            case DomainType.ClientPortal:
                return `https://__tenant__/api/portal/clients/operations/files`;
            case DomainType.ContractorPortal:
                return `https://__tenant__/api/portal/contractor/operations/workorder/files`;
            default:
                return '/api/attachments/metadata';
        }
    }


    private readonly http = inject(HttpClient);


    constructor() { }


    public getFiles(entity: ActivityEntityName, entityId: string, showAll?: boolean): void {

        this.http
            .get<MetadataAttachment[]>(`${this.api}/${entity}/${entityId}?showAll=${showAll}`)
            .pipe(
                tap(x => this._files.next(x))
            )
            .subscribe();
    }

    public getByIds(ids: string[]): MetadataAttachment[] {

        return Enumerable
            .from(this.files)
            .where(x => ids.includes(x._id))
            .toArray();

    }

    public addActivity(activity: Activity): void {

        this.http
            .post<MetadataAttachment[]>(`${this.api}/activity`, activity)
            .pipe(
                tap(attachments => {
                    const files = this._files.value;
                    this._files.next([...files, ...attachments]);
                })
            )
            .subscribe();
    }

    public updateActivity(activityId: string, activity: Partial<Activity>): Observable<MetadataAttachment[]> {

        return this.http
            .post<MetadataAttachment[]>(`${this.api}/activity/${activityId}`, activity);
    }

    public updateActivityLabels(activityId: string, labels: Entity[]): void {

        this.http
            .patch<MetadataAttachment[]>(`${this.api}/activity/${activityId}/labels`, labels)
            .pipe(
                tap(attachments => {
                    this.raiseUpdatedItems(activityId, attachments);
                })
            )
            .subscribe();
    }

    public updateActivitySharing(activityId: string, metadata: Partial<MetadataAttachment>): void {

        this.http
            .patch<MetadataAttachment[]>(`${this.api}/activity/${activityId}/sharing`, metadata)
            .pipe(
                tap(attachments => {
                    this.raiseUpdatedItems(activityId, attachments);
                })
            )
            .subscribe();
    }

    public deleteActivityAttachment(activityId: string, attachmentId: string): Observable<ItemDeletedResponse> {

        return this.http
            .delete<ItemDeletedResponse>(`${this.api}/activity/${activityId}/${attachmentId}`)
            .pipe(
                tap(response => {

                    if (!response.success) {
                        throw new Error(response.error);
                    }

                    const files = this._files.value?.filter(x => x._id !== attachmentId);
                    this._files.next([...files]);
                })
            );
    }

    private raiseUpdatedItems(activityId: string, attachments: MetadataAttachment[]) {
        const files = this._files.value;
        const affectedFiles = Enumerable
            .from(files)
            .where(x => x.entityId === activityId)
            .toObject(x => x._id, x => x);

        Enumerable
            .from(attachments)
            .forEach(x => {

                const affectedFile = affectedFiles[x._id] as MetadataAttachment;

                if (!affectedFile) {
                    return;
                }

                affectedFile.tags = x.tags;
                affectedFile.sensitive = x.sensitive;
                affectedFile.shared = x.shared;
                affectedFile.systemSharing = x.systemSharing;

                if (x.effectiveSharing) {
                    const originalEffectiveSharing = affectedFile.originalEffectiveSharing;

                    if (originalEffectiveSharing) {
                        // Multi-reference file: effective sharing is the union across every reference,
                        // so preserve the original (other references') sharing and merge in this update.
                        affectedFile.effectiveSharing ??= { ...x.effectiveSharing };

                        if (!originalEffectiveSharing.sensitive) {
                            affectedFile.effectiveSharing.sensitive = x.effectiveSharing.sensitive;
                        }

                        const shares = [...(originalEffectiveSharing.shared ?? [])];

                        for (const share of x.effectiveSharing.shared ?? []) {
                            if (!shares.includes(share)) {
                                shares.push(share);
                            }
                        }

                        affectedFile.effectiveSharing.shared = shares;
                    } else {
                        // Single-reference file: the server response is authoritative, so reflect it
                        // directly. This also surfaces un-sharing, which a union-merge would miss.
                        affectedFile.effectiveSharing = { ...x.effectiveSharing };
                    }
                }

                this._fileChanged.next(affectedFile);
            });

        this._files.next([...files]);
    }
}
